import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { useAppTheme } from '../../contexts/ThemeContext';

const STEPS = ['Welcome', 'Business Setup', 'Ready'];

export default function OnboardingScreen({ navigation }) {
  const { colors, spacing, typography, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, typography, radius), [colors]);

  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    markup: '20',
    taxRate: '8.5',
    address: '',
    paymentTerms: 'Net 30',
  });

  function updateSetting(key, val) {
    setSettings((s) => ({ ...s, [key]: val }));
  }

  async function saveSettings() {
    setLoading(true);
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        await supabase.from('user_settings').upsert({
          user_id: currentUser.id,
          default_markup: parseFloat(settings.markup) || 20,
          default_tax_rate: parseFloat(settings.taxRate) || 8.5,
          business_address: settings.address,
          payment_terms: settings.paymentTerms,
        }, { onConflict: 'user_id' });
      }
      setStep(2);
    } catch (err) {
      console.warn('Settings save error:', err.message);
      setStep(2);
    } finally {
      setLoading(false);
    }
  }

  function handleNext() {
    if (step === 0) {
      setStep(1);
    } else if (step === 1) {
      saveSettings();
    } else {
      // Session is active — NavigationContainer will switch to main app automatically.
      // Refresh session to ensure auth state propagates to the navigation container.
      supabase.auth.refreshSession().catch(() => {});
    }
  }

  return (
    <SafeAreaView style={styles.safe}>
      {/* Step indicator */}
      <View style={styles.stepIndicator}>
        {STEPS.map((_, i) => (
          <View
            key={i}
            style={[
              styles.stepDot,
              i === step && styles.stepDotActive,
              i < step && styles.stepDotDone,
            ]}
          />
        ))}
      </View>

      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
      >
        {step === 0 && <WelcomeStep styles={styles} colors={colors} />}
        {step === 1 && (
          <BusinessStep styles={styles} settings={settings} updateSetting={updateSetting} />
        )}
        {step === 2 && <ReadyStep styles={styles} />}
      </ScrollView>

      <View style={styles.actions}>
        {step === 2 ? (
          <Button
            title="Start Your First Quote"
            onPress={handleNext}
            size="lg"
          />
        ) : (
          <Button
            title={step === 1 ? 'Save & Continue' : 'Get Started'}
            onPress={handleNext}
            loading={loading}
            size="lg"
          />
        )}
        {step === 1 && (
          <TouchableOpacity
            style={styles.skipBtn}
            onPress={() => setStep(2)}
            activeOpacity={0.7}
          >
            <Text style={styles.skipText}>Skip for now</Text>
          </TouchableOpacity>
        )}
      </View>
    </SafeAreaView>
  );
}

function WelcomeStep({ styles, colors }) {
  const valueProps = [
    { icon: '⚡', title: 'AI-powered estimates', desc: 'Generate detailed line items in seconds' },
    { icon: '📊', title: 'Track every quote', desc: 'See what\'s pending, accepted, and earned' },
    { icon: '👥', title: 'Client management', desc: 'Keep all your client history in one place' },
  ];

  return (
    <View style={styles.stepContent}>
      <View style={styles.logoMark}>
        <Text style={styles.logoMarkText}>S</Text>
      </View>
      <Text style={styles.welcomeTitle}>Your quotes,{'\n'}sharper.</Text>
      <Text style={styles.welcomeSubtitle}>
        Slate helps contractors win more work with professional quotes built in minutes, not hours.
      </Text>
      <View style={styles.valueProps}>
        {valueProps.map((vp) => (
          <View key={vp.title} style={styles.valueProp}>
            <Text style={styles.valuePropIcon}>{vp.icon}</Text>
            <View style={styles.valuePropText}>
              <Text style={styles.valuePropTitle}>{vp.title}</Text>
              <Text style={styles.valuePropDesc}>{vp.desc}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

function BusinessStep({ styles, settings, updateSetting }) {
  return (
    <View style={styles.stepContent}>
      <Text style={styles.stepTitle}>Business Setup</Text>
      <Text style={styles.stepSubtitle}>
        Set your defaults — you can change these anytime in Settings.
      </Text>

      <Input
        label="Default Markup %"
        value={settings.markup}
        onChangeText={(t) => updateSetting('markup', t)}
        placeholder="20"
        keyboardType="decimal-pad"
      />

      <Input
        label="Default Tax Rate %"
        value={settings.taxRate}
        onChangeText={(t) => updateSetting('taxRate', t)}
        placeholder="8.5"
        keyboardType="decimal-pad"
      />

      <Input
        label="Business Address"
        value={settings.address}
        onChangeText={(t) => updateSetting('address', t)}
        placeholder="123 Main St, City, State 12345"
        multiline
        numberOfLines={2}
      />

      <Input
        label="Payment Terms"
        value={settings.paymentTerms}
        onChangeText={(t) => updateSetting('paymentTerms', t)}
        placeholder="Net 30"
      />
    </View>
  );
}

function ReadyStep({ styles }) {
  return (
    <View style={[styles.stepContent, styles.readyContent]}>
      <Text style={styles.readyEmoji}>🎉</Text>
      <Text style={styles.readyTitle}>You're all set.</Text>
      <Text style={styles.readySubtitle}>
        Your account is ready. Start by creating your first quote — the AI will handle the heavy lifting.
      </Text>
      <View style={styles.readyTips}>
        <Text style={styles.tipHeading}>Quick tips</Text>
        <Text style={styles.tip}>→ Describe the job in plain English</Text>
        <Text style={styles.tip}>→ Review and edit AI-generated items</Text>
        <Text style={styles.tip}>→ Adjust markup before sending</Text>
      </View>
    </View>
  );
}

function createStyles(colors, spacing, typography, radius) {
  return StyleSheet.create({
    safe: {
      flex: 1,
      backgroundColor: colors.background,
    },
    stepIndicator: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingTop: spacing.xl,
      paddingBottom: spacing.base,
      gap: spacing.sm,
    },
    stepDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: colors.border,
    },
    stepDotActive: {
      width: 20,
      backgroundColor: colors.accent,
      borderRadius: 3,
    },
    stepDotDone: {
      backgroundColor: colors.accentDark,
      opacity: 0.5,
    },
    scroll: {
      flexGrow: 1,
      paddingHorizontal: spacing.xl,
    },
    stepContent: {
      flex: 1,
      paddingTop: spacing.xxl,
      paddingBottom: spacing.xxl,
    },
    logoMark: {
      width: 72,
      height: 72,
      borderRadius: 20,
      backgroundColor: colors.accent,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: spacing.xl,
      shadowColor: colors.accent,
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.4,
      shadowRadius: 16,
      elevation: 10,
    },
    logoMarkText: {
      fontSize: 36,
      fontWeight: typography.weights.bold,
      color: colors.text,
      letterSpacing: -1,
    },
    welcomeTitle: {
      fontSize: typography.sizes.xxxl,
      fontWeight: typography.weights.bold,
      color: colors.text,
      letterSpacing: -1.5,
      lineHeight: typography.sizes.xxxl * 1.15,
      marginBottom: spacing.base,
    },
    welcomeSubtitle: {
      fontSize: typography.sizes.md,
      color: colors.textSecondary,
      lineHeight: typography.sizes.md * 1.6,
      marginBottom: spacing.xxxl,
    },
    valueProps: {
      gap: spacing.base,
    },
    valueProp: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.base,
      gap: spacing.md,
    },
    valuePropIcon: {
      fontSize: 22,
      marginTop: 2,
    },
    valuePropText: {
      flex: 1,
    },
    valuePropTitle: {
      fontSize: typography.sizes.base,
      fontWeight: typography.weights.semibold,
      color: colors.text,
      marginBottom: 2,
    },
    valuePropDesc: {
      fontSize: typography.sizes.sm,
      color: colors.textSecondary,
    },
    stepTitle: {
      fontSize: typography.sizes.xxl,
      fontWeight: typography.weights.bold,
      color: colors.text,
      letterSpacing: -0.8,
      marginBottom: spacing.sm,
    },
    stepSubtitle: {
      fontSize: typography.sizes.base,
      color: colors.textSecondary,
      marginBottom: spacing.xl + spacing.sm,
      lineHeight: typography.sizes.base * 1.6,
    },
    readyContent: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: spacing.xxxl + spacing.xl,
    },
    readyEmoji: {
      fontSize: 56,
      marginBottom: spacing.xl,
    },
    readyTitle: {
      fontSize: typography.sizes.xxl + 4,
      fontWeight: typography.weights.bold,
      color: colors.text,
      letterSpacing: -1,
      marginBottom: spacing.md,
      textAlign: 'center',
    },
    readySubtitle: {
      fontSize: typography.sizes.base,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: typography.sizes.base * 1.7,
      marginBottom: spacing.xxxl,
      paddingHorizontal: spacing.md,
    },
    readyTips: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.base,
      alignSelf: 'stretch',
      gap: spacing.sm,
    },
    tipHeading: {
      fontSize: typography.sizes.sm,
      fontWeight: typography.weights.semibold,
      color: colors.textMuted,
      textTransform: 'uppercase',
      letterSpacing: 1,
      marginBottom: spacing.xs,
    },
    tip: {
      fontSize: typography.sizes.base,
      color: colors.textSecondary,
      lineHeight: typography.sizes.base * 1.5,
    },
    actions: {
      paddingHorizontal: spacing.xl,
      paddingBottom: spacing.xl,
      paddingTop: spacing.base,
      gap: spacing.sm,
    },
    skipBtn: {
      alignItems: 'center',
      paddingVertical: spacing.sm,
    },
    skipText: {
      fontSize: typography.sizes.base,
      color: colors.textMuted,
    },
  });
}
